import { useEffect, useState } from "react";
import { Sparkles, Feather, Bot } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export const SCHOOL_EVENT = "cl-school-changed";

const PracticePrompt = ({ open, onPick, sfx, covers }) => (
  <Dialog open={open} onOpenChange={(o) => !o && onPick("keep")}>
    <DialogContent className="bg-[#FDFBF7]" data-testid={`practice-prompt${sfx}`}>
      <DialogTitle className="font-display text-2xl text-[#2C4A3E]">Your school assigns work. Keep ConsciousLearn's daily practice?</DialogTitle>
      <DialogDescription className="text-sm text-stone-600">Daily practice stays on. You can still do Fine Arts, Music, Health & PE and World Languages here, and Buddy helps with every school assignment.</DialogDescription>
      <div className="grid gap-2">
        {[["keep", "Keep everything on", "Recommended · 3 short items a day, as now"],
          ...(covers?.length ? [["skip", `Keep on, skip ${covers.map((c) => c.name).join(" & ")}`, "Your school already assigns these"]] : []),
          ["light", "Lighter mode", "Just 1 practice item a day"],
          ["choose", "Pick subjects", "Keep only the subjects school doesn't cover"], ["off", "Turn it off", "Only school and teacher work"]].map(([k, t, d]) => (
          <button key={k} onClick={() => onPick(k)} data-testid={`practice-prompt-${k}${sfx}`}
            className={`text-left rounded-2xl border p-4 hover:border-[#2C4A3E] transition-colors ${k === "keep" ? "border-[#2C4A3E] bg-[#EEF3EF]" : "border-[#E6DEC3] bg-white"}`}>
            <p className="text-sm font-semibold text-[#2C4A3E]">{t}</p><p className="text-xs text-stone-500 mt-0.5">{d}</p></button>))}
      </div>
    </DialogContent>
  </Dialog>
);

const Chip = ({ s, on, disabled, onClick, sfx }) => (
  <button type="button" disabled={disabled} onClick={onClick} aria-pressed={on} data-testid={`practice-subject-${s.id}${sfx}`}
    className={`px-3 py-1.5 rounded-full text-xs border transition-colors disabled:opacity-40 ${on ? "bg-[#2C4A3E] text-white border-[#2C4A3E]" : "bg-white text-stone-500 border-[#E6DEC3] line-through"}`}>{s.name}</button>
);

export const PracticePanel = ({ studentId = "me", compact = false, onChange }) => {
  const [p, setP] = useState(null);
  const [open, setOpen] = useState(false);
  const [clear, setClear] = useState(false);
  useEffect(() => {
    if (!p?.show_prompt || clear) return;
    const t = setInterval(() => setClear(!document.querySelector('[role="dialog"], [role="alertdialog"]')), 700);
    return () => clearInterval(t);
  }, [p?.show_prompt, clear]);
  const sfx = studentId === "me" ? "" : `-${studentId}`;
  const load = () => http.get(`/practice/${studentId}`).then((r) => setP(r.data)).catch(() => {});
  useEffect(() => { load(); const h = () => load(); window.addEventListener(SCHOOL_EVENT, h); return () => window.removeEventListener(SCHOOL_EVENT, h); }, [studentId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!p) return null;
  const save = async (next, msg) => {
    const prev = p; setP({ ...p, ...next, show_prompt: false });
    try { const r = (await http.put(`/practice/${studentId}`, { enabled: p.enabled, light: p.light, off: p.off, ...next })).data; setP(r); toast.success(msg || "Daily practice updated"); onChange?.(); }
    catch (e) { setP(prev); toast.error(formatApiError(e)); }
  };
  const pick = async (k) => {
    if (k === "light") return save({ enabled: true, light: true }, "Lighter mode on: 1 practice item a day");
    if (k === "off") return save({ enabled: false }, "Daily practice turned off");
    if (k === "skip") return save({ enabled: true, off: [...new Set([...p.off, ...p.school_covers.map((c) => c.id)])] }, "Daily practice stays on for the other subjects");
    setP({ ...p, show_prompt: false }); if (k === "choose") setOpen(true);
    await http.post(`/practice/${studentId}/dismiss`).catch(() => {});
  };
  const toggleSubject = (id) => save({ off: p.off.includes(id) ? p.off.filter((x) => x !== id) : [...p.off, id] });
  return (
    <section className={`${compact ? "mt-4" : "mt-6"} rounded-2xl bg-[#FBF6EC] border border-[#E6DEC3] px-5 py-4`} data-testid={`practice-panel${sfx}`}>
      <div className="flex flex-wrap items-center gap-3">
        <Sparkles className="w-4 h-4 text-[#C87D53]" />
        <button onClick={() => setOpen(!open)} data-testid={`practice-panel-toggle${sfx}`} className="flex-1 text-left text-sm text-[#2C4A3E]">
          <b>ConsciousLearn daily practice</b> · <span data-testid={`practice-status${sfx}`}>{!p.enabled ? "Off" : p.light ? "Lighter (1 a day)" : "On (3 a day)"}{p.enabled && p.off.length ? `, ${p.off.length} subject${p.off.length > 1 ? "s" : ""} off` : ""}</span>
          <span className="ml-2 text-xs underline text-stone-500">{open ? "Hide" : "Change"}</span></button>
        <Switch checked={p.enabled} onCheckedChange={(v) => save({ enabled: v }, v ? "Daily practice turned on" : "Daily practice turned off")} data-testid={`practice-master-switch${sfx}`} aria-label="Daily practice on or off" />
      </div>
      {open && <div className="mt-4 space-y-4" data-testid={`practice-details${sfx}`}>
        <label className="flex items-center gap-3 text-sm text-[#2C4A3E]"><Feather className="w-4 h-4" /> Lighter mode: 1 item a day instead of 3
          <Switch checked={p.light} disabled={!p.enabled} onCheckedChange={(v) => save({ light: v })} data-testid={`practice-light-switch${sfx}`} /></label>
        <div><p className="text-xs uppercase tracking-widest text-stone-500 mb-2">Subjects to practice</p>
          <div className="flex flex-wrap gap-2">{p.subjects.map((s) => <Chip key={s.id} s={s} on={!p.off.includes(s.id)} disabled={!p.enabled} onClick={() => toggleSubject(s.id)} sfx={sfx} />)}</div></div>
        <p className="text-xs text-stone-500 inline-flex items-start gap-1.5"><Bot className="w-3.5 h-3.5 mt-0.5 shrink-0" /> Buddy help stays on for every assignment, including school and teacher work. Changes replace upcoming unfinished practice; finished work is kept.</p>
      </div>}
      <PracticePrompt open={!!p.show_prompt && clear} onPick={pick} sfx={sfx} covers={p.school_covers} />
    </section>
  );
};
